import React, { useState, useEffect } from 'react';
import {
  TrendingUp, Plus, Eye, Edit, Trash2, ExternalLink,
  FileText, AlertCircle, CheckCircle, BarChart, Target,
  Clock, Send, Check, X, ArrowRight, ChevronDown, ChevronRight
} from 'lucide-react';
import axiosInstance from '../../config/axios';
import { useContentFilters } from '../../hooks/useContentFilters';
import ContentFilterBar from './ContentFilterBar';
import ContentGroupView from './ContentGroupView';
import SEOAssetCreation from '../assets/by-type/SEOAssetCreation';

interface SEOContent {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'pending_review' | 'approved' | 'published' | 'rejected';
  metadata?: string; // JSON string containing seo_score, meta_title, meta_description, target_keywords, etc.
  scheduled_for?: string;
  created_by?: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  updated_at?: string;
  // Parsed from metadata
  content_type?: string;
  workflow_status?: string;
  url?: string;
  client_name?: string;
  project_name?: string;
  project_id?: number;
  phase_name?: string;
  phase_id?: number;
  assigned_to?: string;
  priority?: string;
  tags?: string[];
  seo_score?: number;
  meta_title?: string;
  meta_description?: string;
  target_keywords?: string[];
  word_count?: number;
  images_count?: number;
  target_date?: string;
}

const WORKFLOW_STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'gray', icon: Edit },
  am_review: { label: 'AM Review', color: 'yellow', icon: Clock },
  am_approved: { label: 'AM Approved', color: 'blue', icon: Check },
  client_review: { label: 'Client Review', color: 'purple', icon: Send },
  client_approved: { label: 'Client Approved', color: 'green', icon: Check },
  in_production: { label: 'In Production', color: 'indigo', icon: AlertCircle },
  completed: { label: 'Completed', color: 'green', icon: Check },
  rejected: { label: 'Rejected', color: 'red', icon: X },
};

export default function SEOManagement() {
  const [seoContent, setSeoContent] = useState<SEOContent[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  const {
    filters,
    grouping,
    filteredItems,
    groupedItems,
    activeFilterCount,
    updateFilter,
    clearFilters,
    removeFilter,
    setGrouping,
    isGrouped,
  } = useContentFilters(seoContent, 'website');

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  useEffect(() => {
    loadSEOContent();
  }, []);

  const loadSEOContent = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/content?type=website');
      if (response.data.items) {
        // Parse metadata for each SEO item
        const parsedItems = response.data.items.map((item: any) => {
          let metadata = {};
          try {
            metadata = item.metadata ? JSON.parse(item.metadata) : {};
          } catch (e) {
            console.warn('Failed to parse metadata for SEO item:', item.id);
          }
          return {
            ...item,
            content_type: metadata.content_type || 'page',
            seo_score: metadata.seo_score || 0,
            meta_title: metadata.meta_title || item.title,
            meta_description: metadata.meta_description || item.description,
            target_keywords: metadata.target_keywords || [],
            word_count: metadata.word_count || 0,
            url: metadata.url || '',
            ...metadata
          };
        });
        setSeoContent(parsedItems);
      }
    } catch (error) {
      console.error('Failed to load SEO content:', error);
      setSeoContent([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  };

  const handleWorkflowStatusChange = async (itemId: number, newStatus: string) => {
    try {
      await axios.post(`${API_BASE_URL}/content/${itemId}/workflow-status`, {
        workflow_status: newStatus,
      });
      await loadSEOContent();
    } catch (error) {
      console.error('Failed to update workflow status:', error);
      alert('Failed to update workflow status');
    }
  };

  const getSEOScoreColor = (score?: number) => {
    if (!score) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getSEOScoreBg = (score?: number) => {
    if (!score) return 'bg-gray-100';
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'published':
        return <span className="flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
          <CheckCircle className="h-3 w-3 mr-1" /> Published
        </span>;
      case 'draft':
        return <span className="flex items-center px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
          <FileText className="h-3 w-3 mr-1" /> Draft
        </span>;
      case 'pending_review':
        return <span className="flex items-center px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
          <AlertCircle className="h-3 w-3 mr-1" /> Pending Review
        </span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">{status}</span>;
    }
  };

  const getWorkflowStatusColor = (status?: string) => {
    if (!status) return 'bg-gray-100 text-gray-700';
    const config = WORKFLOW_STATUS_CONFIG[status as keyof typeof WORKFLOW_STATUS_CONFIG];
    return config ? `bg-${config.color}-100 text-${config.color}-700` : 'bg-gray-100 text-gray-700';
  };

  const renderWorkflowActions = (item: SEOContent) => {
    const status = item.workflow_status;

    if (status === 'draft') {
      return (
        <button
          onClick={() => handleWorkflowStatusChange(item.id, 'am_review')}
          className="px-3 py-1 text-xs font-medium text-yellow-700 bg-yellow-50 rounded hover:bg-yellow-100 flex items-center gap-1"
        >
          Submit to AM <ArrowRight className="h-3 w-3" />
        </button>
      );
    }

    if (status === 'am_review') {
      return (
        <div className="flex gap-1">
          <button
            onClick={() => handleWorkflowStatusChange(item.id, 'am_approved')}
            className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
          >
            <Check className="h-3 w-3" />
          </button>
          <button
            onClick={() => handleWorkflowStatusChange(item.id, 'rejected')}
            className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      );
    }

    if (status === 'am_approved') {
      return (
        <button
          onClick={() => handleWorkflowStatusChange(item.id, 'client_review')}
          className="px-3 py-1 text-xs font-medium text-purple-700 bg-purple-50 rounded hover:bg-purple-100 flex items-center gap-1"
        >
          Send to Client <ArrowRight className="h-3 w-3" />
        </button>
      );
    }

    if (status === 'client_review') {
      return (
        <div className="flex gap-1">
          <button
            onClick={() => handleWorkflowStatusChange(item.id, 'client_approved')}
            className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
          >
            Approved
          </button>
          <button
            onClick={() => handleWorkflowStatusChange(item.id, 'rejected')}
            className="px-3 py-1 text-xs font-medium text-red-700 bg-red-50 rounded hover:bg-red-100"
          >
            Rejected
          </button>
        </div>
      );
    }

    if (status === 'client_approved') {
      return (
        <button
          onClick={() => handleWorkflowStatusChange(item.id, 'in_production')}
          className="px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 rounded hover:bg-indigo-100 flex items-center gap-1"
        >
          Start Production <ArrowRight className="h-3 w-3" />
        </button>
      );
    }

    if (status === 'in_production') {
      return (
        <button
          onClick={() => handleWorkflowStatusChange(item.id, 'completed')}
          className="px-3 py-1 text-xs font-medium text-green-700 bg-green-50 rounded hover:bg-green-100"
        >
          Complete
        </button>
      );
    }

    return null;
  };

  const renderSEOContentItem = (item: SEOContent) => {
    const workflowConfig = item.workflow_status
      ? WORKFLOW_STATUS_CONFIG[item.workflow_status as keyof typeof WORKFLOW_STATUS_CONFIG]
      : null;
    const WorkflowIcon = workflowConfig?.icon || Clock;

    return (
      <div className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-200 p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-start space-x-4">
              <div className="h-12 w-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <h3 className="text-lg font-semibold text-gray-900">{item.title}</h3>
                  {getStatusBadge(item.status)}
                </div>

                {item.client_name && (
                  <p className="text-sm text-gray-500 mb-2">{item.client_name}</p>
                )}

                {item.meta_description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.meta_description}</p>
                )}

                {item.target_keywords && item.target_keywords.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    <Target className="h-4 w-4 text-gray-400 mr-1" />
                    {item.target_keywords.slice(0, 5).map((keyword, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                        {keyword}
                      </span>
                    ))}
                    {item.target_keywords.length > 5 && (
                      <span className="text-xs text-gray-500">+{item.target_keywords.length - 5} more</span>
                    )}
                  </div>
                )}

                {/* Workflow Status & Actions */}
                {item.workflow_status && (
                  <div className="flex items-center gap-2 mb-2 pt-2 border-t border-gray-100">
                    <span className="text-xs font-medium text-gray-500">Workflow:</span>
                    <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded ${getWorkflowStatusColor(item.workflow_status)}`}>
                      <WorkflowIcon className="h-3 w-3" />
                      {workflowConfig?.label || item.workflow_status}
                    </span>
                    {renderWorkflowActions(item)}
                  </div>
                )}

                <div className="flex items-center space-x-4 text-xs text-gray-500">
                  {item.word_count && (
                    <span className="flex items-center">
                      <FileText className="h-3 w-3 mr-1" />
                      {item.word_count.toLocaleString()} words
                    </span>
                  )}
                  {item.target_date && (
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      Due: {new Date(item.target_date).toLocaleDateString()}
                    </span>
                  )}
                  {item.url && (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="flex items-center hover:text-blue-600">
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Live
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-start space-x-4 ml-4">
            {item.seo_score !== undefined && (
              <div className={`${getSEOScoreBg(item.seo_score)} rounded-lg p-4 text-center min-w-[80px]`}>
                <BarChart className={`h-5 w-5 ${getSEOScoreColor(item.seo_score)} mx-auto mb-1`} />
                <p className={`text-2xl font-bold ${getSEOScoreColor(item.seo_score)}`}>
                  {item.seo_score}
                </p>
                <p className="text-xs text-gray-600">SEO Score</p>
              </div>
            )}

            <div className="flex flex-col space-y-2">
              <button className="flex items-center px-3 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition text-sm font-medium">
                <Eye className="h-4 w-4 mr-1.5" />
                Preview
              </button>
              <button className="flex items-center px-3 py-2 bg-gray-50 text-gray-700 rounded-lg hover:bg-gray-100 transition text-sm font-medium">
                <Edit className="h-4 w-4 mr-1.5" />
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SEO & Content Workflow</h1>
          <p className="mt-1 text-sm text-gray-500">
            SEO optimization tracking with integrated approval workflow
          </p>
        </div>
        <button className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition">
          <Plus className="h-5 w-5 mr-2" />
          New SEO Item
        </button>
      </div>

      {/* Filters */}
      <ContentFilterBar
        contentType="website"
        filters={filters}
        grouping={grouping}
        activeFilterCount={activeFilterCount}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        onRemoveFilter={removeFilter}
        onGroupingChange={(updates) => setGrouping({ ...grouping, ...updates })}
        clients={[]}
        projects={[]}
        phases={[]}
      />

      {/* Content Display - Table View */}
      {!isGrouped && filteredItems.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SEO Score</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keywords</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 p-2 bg-green-50 rounded-lg">
                          <TrendingUp className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{item.title}</div>
                          <div className="text-xs text-gray-500 truncate max-w-md">{item.meta_description || item.description}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.client_name || `Client #${item.client_id}`}</div>
                      <div className="text-xs text-gray-500">{item.project_name || 'No project'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(item.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <BarChart className={`h-4 w-4 mr-2 ${getSEOScoreColor(item.seo_score || 0)}`} />
                        <span className={`text-sm font-medium ${getSEOScoreColor(item.seo_score || 0)}`}>
                          {item.seo_score || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {item.target_keywords && item.target_keywords.length > 0 ? (
                          <>
                            {item.target_keywords.slice(0, 3).map((keyword, idx) => (
                              <span key={idx} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">
                                {keyword}
                              </span>
                            ))}
                            {item.target_keywords.length > 3 && (
                              <span className="text-xs text-gray-500">+{item.target_keywords.length - 3}</span>
                            )}
                          </>
                        ) : (
                          <span className="text-xs text-gray-400">No keywords</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        {item.url && (
                          <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        )}
                        <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                          <Eye className="h-4 w-4" />
                        </button>
                        <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                          <Edit className="h-4 w-4" />
                        </button>
                        <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grouped View */}
      {isGrouped && (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([groupName, groupSEO]) => {
            const seoArray = Array.isArray(groupSEO) ? groupSEO : [];
            const isExpanded = expandedGroups.has(groupName);

            return (
            <div key={groupName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
              >
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {groupName} ({seoArray.length})
                </h3>
              </button>
              {isExpanded && (
              <div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Page</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SEO Score</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Keywords</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {seoArray.map((seo: SEOContent) => (
                      <tr key={seo.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 p-2 bg-green-50 rounded-lg">
                              <TrendingUp className="h-5 w-5 text-green-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{seo.title}</div>
                              {seo.url && (
                                <a href={seo.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1">
                                  {seo.url} <ExternalLink className="h-3 w-3" />
                                </a>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{seo.client_name || `Client #${seo.client_id || 'N/A'}`}</div>
                          <div className="text-xs text-gray-500">{seo.project_name || 'No project'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(seo.status)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className={`w-16 h-2 rounded-full ${getSEOScoreBg(seo.seo_score)}`}>
                              <div className="h-full rounded-full bg-current" style={{ width: `${seo.seo_score || 0}%` }} />
                            </div>
                            <span className="text-sm font-medium text-gray-900">{seo.seo_score || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-1">
                            {seo.target_keywords?.slice(0, 3).map((keyword, idx) => (
                              <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                {keyword}
                              </span>
                            ))}
                            {seo.target_keywords && seo.target_keywords.length > 3 && (
                              <span className="text-xs text-gray-500">+{seo.target_keywords.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center space-x-2">
                            <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                              <Eye className="h-4 w-4" />
                            </button>
                            <button className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded">
                              <Edit className="h-4 w-4" />
                            </button>
                            <button className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {!isGrouped && filteredItems.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <TrendingUp className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No SEO content found</h3>
          <p className="text-gray-500 mb-6">Start tracking your SEO optimization and workflow</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create SEO Item
          </button>
        </div>
      )}

      {/* Create SEO Modal - Full Screen */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Create SEO Content</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  loadSEOContent(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render SEO Asset Creation Component */}
            <div className="p-6">
              <SEOAssetCreation />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
